/**
 * SectionRenderer — section
 * Section divider: large section number + heading + optional body.
 */
import React from "react";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";

function fieldAnims(field) {
  if (!field || typeof field !== "object") return [null, null];
  return [field.boxAnimation ?? null, field.boxExitAnimation ?? null];
}

export function SectionRenderer({ element, editing, onChange, playing, isPaused, currentTimeSec }) {
  const sectionNumber = element.sectionNumber ? resolveTextOptional(element.sectionNumber) : null;
  const heading = resolveText(element.heading);
  const body = element.body ? resolveTextOptional(element.body) : null;

  const [numBox, numExit] = fieldAnims(element.sectionNumber);
  const [headingBox, headingExit] = fieldAnims(element.heading);
  const [bodyBox, bodyExit] = fieldAnims(element.body);

  return (
    <div style={{ display: "grid", gap: "var(--space-3)" }}>
      {/* Section number */}
      {sectionNumber !== null && (
        <AnimatedField
          boxAnimation={numBox}
          boxExitAnimation={numExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <span
            style={{
              fontFamily: "var(--font-heading)",
              fontWeight: "var(--font-display-weight)",
              fontSize: "var(--text-4xl)",
              color: "var(--color-text-accent)",
              lineHeight: 1,
              opacity: 0.6,
            }}
          >
            <EditableText
              value={sectionNumber || ""}
              editing={editing}
              onChange={(val) => onChange("sectionNumber", val)}
            />
          </span>
        </AnimatedField>
      )}

      {/* Accent line */}
      <div
        style={{
          width: "48px",
          height: "3px",
          background: "var(--color-text-accent)",
          borderRadius: "var(--radius-full)",
        }}
      />

      <AnimatedField
        boxAnimation={headingBox}
        boxExitAnimation={headingExit}
        playing={playing}
        isPaused={isPaused}
        currentTimeSec={currentTimeSec}
      >
        <Title size="3xl">
          <EditableText
            value={heading || ""}
            editing={editing}
            onChange={(val) => onChange("heading", val)}
          />
        </Title>
      </AnimatedField>

      {body !== null && (
        <AnimatedField
          boxAnimation={bodyBox}
          boxExitAnimation={bodyExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <Text role="large">
            <EditableText
              value={body || ""}
              editing={editing}
              onChange={(val) => onChange("body", val)}
            />
          </Text>
        </AnimatedField>
      )}
    </div>
  );
}

export default SectionRenderer;
